import React, { useState, useEffect } from "react";
import { apiService } from "../../services";

interface Batch {
  _id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface ExportCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (batchId: string) => Promise<void>;
  isLoading?: boolean;
}

const ExportCodeModal: React.FC<ExportCodeModalProps> = ({
  isOpen,
  onClose,
  onExport,
  isLoading = false,
}) => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<string>("");
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);

  // Fetch batches when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchBatches();
    }
  }, [isOpen]);

  const fetchBatches = async () => {
    try {
      setIsLoadingBatches(true);
      const response = await apiService.getBatches();
      if (response.success) {
        setBatches(response.data);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
    } finally {
      setIsLoadingBatches(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBatch) {
      return;
    }
    await onExport(selectedBatch);
  };

  const handleClose = () => {
    setSelectedBatch("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Xuất file mã code
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              disabled={isLoading}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-4">
          <div className="space-y-4">
            {/* Batch Selection */}
            <div>
              <label
                htmlFor="batch"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Chọn batch để xuất mã code
              </label>
              <select
                id="batch"
                value={selectedBatch}
                onChange={(e) => setSelectedBatch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                disabled={isLoadingBatches || isLoading}
              >
                <option value="">
                  {isLoadingBatches
                    ? "Đang tải danh sách batch..."
                    : "Chọn batch để xuất mã code"}
                </option>
                {batches.map((batch) => (
                  <option key={batch._id} value={batch._id}>
                    {batch.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              disabled={isLoading}
            >
              Hủy
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 dark:hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={!selectedBatch || isLoading}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Đang xuất file...
                </div>
              ) : (
                "Xuất file mã code"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExportCodeModal;
